import { UseQueryResult } from '@tanstack/react-query'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { WidgetCardBody, getProgressBarColor } from '@pages/Trade/Analytics/Analytics.components'
import { TabWithQuery, WidgetCardProps } from '@pages/Trade/Analytics/Analytics.types'
import { TopAnalyticsItem } from '@pages/Trade/Analytics/hooks/useAnalyticsFetch'
import {
  MarketDataBridge,
  updateCurrentPricesFromMarketData,
} from '@pages/Trade/Portfolio/components/MarketDataBridge'

import { BaseModalType } from '@context/ModalsContext'

import { Title1, Title2 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'
import { LogoIcon } from '@ui/icons/LogoIcon'

import { Box } from '@shared/components/Box'

interface ShowMoreModalProps {
  title: string
  activeTab?: string
  tabs?: TabWithQuery[]
  query: UseQueryResult<TopAnalyticsItem[], Error>
  onClick(symbol: string): void
}

const ShowMoreModal: BaseModalType<ShowMoreModalProps> = (props) => {
  const { title, activeTab, tabs = [], query, close, onClick } = props

  const handleInstrumentClick = useCallback(
    (symbol: string) => {
      close()
      onClick(symbol)
    },
    [close]
  )

  const symbols = useMemo(() => {
    if (tabs.length > 0) {
      return tabs
        .filter((tab) => hasData(tab.query))
        .flatMap((tab) => (tab.query.data || []).map((item) => item.instrumentSymbol))
        .filter(Boolean) as string[]
    }

    return hasData(query)
      ? ((query.data || []).map((item) => item.instrumentSymbol).filter(Boolean) as string[])
      : []
  }, [tabs, query])

  if (tabs.length > 0) {
    const initialTabIndex = Math.max(
      0,
      tabs.findIndex((tab) => tab.id === activeTab)
    )

    return (
      <>
        <Modal>
          <Modal.Header canClose>{title}</Modal.Header>
          <Modal.Body>
            <Modal.Body.Tabs
              attachToHeader
              initialIndex={initialTabIndex}
              variant='segmented-control'
              fullWidth
            >
              {tabs.map((tab) => (
                <Modal.Body.Tabs.Tab key={tab.id} id={tab.id} title={tab.name}>
                  {hasData(tab.query) ? (
                    <WidgetCardBody
                      data={tab.query.data || []}
                      progressBarColor={getProgressBarColor(tab.id)}
                      limit={10}
                      onInstrumentClick={handleInstrumentClick}
                    />
                  ) : (
                    <WidgetCardEmptyModal {...props} />
                  )}
                </Modal.Body.Tabs.Tab>
              ))}
            </Modal.Body.Tabs>
          </Modal.Body>
        </Modal>
        <MarketDataBridge symbols={symbols} onTick={updateCurrentPricesFromMarketData} />
      </>
    )
  }

  return (
    <Modal>
      <Modal.Header canClose>{title}</Modal.Header>
      <Modal.Body>
        <Modal.Body.Tabs.Tab id={title} title={title}>
          <WidgetCardBody
            data={query.data || []}
            limit={10}
            onInstrumentClick={handleInstrumentClick}
          />
          <MarketDataBridge symbols={symbols} onTick={updateCurrentPricesFromMarketData} />
        </Modal.Body.Tabs.Tab>
      </Modal.Body>
    </Modal>
  )
}
export default ShowMoreModal

const WidgetCardEmptyModal: React.FC<WidgetCardProps> = () => {
  const { t } = useTranslation()

  return (
    <Box flex direction='column'>
      <Box px='x20' pt='x12' pb='x20'>
        <Box textAlign='center' pb='x12'>
          <LogoIcon color='text-med-em' secondaryColor='text-med-em' size={48} />
        </Box>
        <Box textAlign='center' pb='x6'>
          <Title1 medium color='text-high-em'>
            {t('Analytics.No data yet')}
          </Title1>
        </Box>
        <Box textAlign='center'>
          <Title2 color='text-low-em'>
            {t('Analytics.We’ll highlight the most important market trends here as they happen')}
          </Title2>
        </Box>
      </Box>
    </Box>
  )
}

const hasData = (
  query?: UseQueryResult<TopAnalyticsItem[], Error>
): query is UseQueryResult<TopAnalyticsItem[], Error> & { data: TopAnalyticsItem[] } => {
  return !!query && !query.isError && Array.isArray(query.data) && query.data.length > 0
}
